<script setup lang="ts">
import LoyaltyCreateContainer from './LoyaltyCreateContainer.vue'
import LoyaltyCreateHelpBlock from './LoyaltyCreateHelpBlock.vue'
import PartnerStoresEmptyState from '@/views/marketplace/components/PartnerStoresEmptyState.vue'

const {
  selectedPartner,
  isAssociatedArray,
  partnerDevices,
  selectedStores,
  storesValues,
  updateStore,
  getPartnerDevices,
  updateSelectedStores,
  posDeviceId
} = useLoyalty()
const { stores } = useLocalStorage()
const { t } = useI18n()
const logoUrl = `${import.meta.env.VITE_AWS_STORAGE_URL}/logo-violet.png`
const isLoading = ref(false)

const handleDeviceAssociation = async (id: number) => {
  if (isAssociatedArray.value.includes(id)) {
    const index = isAssociatedArray.value.findIndex((value) => value === id)
    if (index > -1) {
      isAssociatedArray.value.splice(index, 1)
    }
  } else {
    isAssociatedArray.value.push(id)
  }
}

isAssociatedArray.value = [...partnerDevices.value.map((p) => p.id)]

const handleRefresh = async () => {
  isLoading.value = true

  try {
    await getPartnerDevices()
    updateSelectedStores()
    isAssociatedArray.value = [...partnerDevices.value.map((p) => p.id)]
    isLoading.value = false
  } catch {
    isLoading.value = false
  }
}

handleRefresh()
</script>

<template>
  <LoyaltyCreateContainer content-type="full-content">
    <LoyaltyCreateHelpBlock :selected-partner="selectedPartner" />
    <div v-if="partnerDevices.length === 0" class="h-full w-full overflow-y-auto px-10 pt-10 xl:mb-28">
      <div class="mb-5 flex h-2/3 flex-col items-center justify-center rounded-md bg-muted text-foreground">
        <PartnerStoresEmptyState
          :auth-type="selectedPartner.partner.partnerAuthenticationType?.slug"
          :is-loading="isLoading"
          @on-click="handleRefresh"
        />
      </div>
      <div class="mb-3 text-left text-sm font-medium text-foreground">
        Si vous connaissez l'identifiant de votre caisse, vous pouvez l'enregistrer ici
      </div>
      <div class="w-full overflow-x-auto">
        <div class="flex w-full min-w-[700px] justify-center rounded-md bg-muted p-10">
          <div class="inline-block w-full">
            <table class="w-full text-left">
              <tbody>
                <tr>
                  <th class="py-2">
                    <div class="flex items-center gap-2">
                      <img
                        class="aspect-square w-6 rounded-xs border bg-white object-contain p-1"
                        :src="selectedPartner?.partner?.logo"
                      />
                      <h3 class="text-left font-semibold">{{ t('pages.loyalty.create.instructions.softName') }}</h3>
                    </div>
                  </th>
                  <th></th>
                  <th class="py-2">
                    <div class="flex items-center gap-2">
                      <img class="aspect-square w-6 rounded-xs border bg-white object-contain p-0.5" :src="logoUrl" />
                      <h3 class="text-left font-semibold">{{ t('pages.loyalty.create.instructions.pongoStore') }}</h3>
                    </div>
                  </th>
                </tr>
                <tr v-for="store in stores" :key="store.id">
                  <td class="w-1/3 py-2">
                    <AppFormInput id="posDeviceId" v-model="posDeviceId[store.id]" type="text" placeholder="123456" />
                  </td>
                  <td class="relative w-1/3">
                    <div class="absolute top-1/2 z-0 w-full border-t border-dashed border-border" />
                    <div
                      class="absolute left-1/2 top-1/2 z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full border bg-white p-1"
                    >
                      <BaseIcon name="HeroArrowsRightLeftIconOutline" class="h-5 w-5 text-foreground-disabled" />
                    </div>
                  </td>
                  <td class="w-1/3 py-2">
                    <AppFormInput :id="store.id" v-model="store.name" :disabled="true" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="min-h-[250px] w-full overflow-x-auto px-10">
      <div class="mt-10 w-full min-w-[800px] rounded-md bg-muted p-10">
        <div class="inline-block w-full">
          <table class="w-full text-left">
            <tbody>
              <tr>
                <th class="py-2">
                  <div class="flex items-center gap-2">
                    <img
                      class="aspect-square w-6 rounded-xs border bg-white object-contain p-1"
                      :src="selectedPartner?.partner?.logo"
                    />
                    <h3 class="text-left font-semibold">{{ t('pages.loyalty.create.instructions.softName') }}</h3>
                  </div>
                </th>
                <th></th>
                <th class="py-2">
                  <div class="flex items-center gap-2">
                    <img class="aspect-square w-6 rounded-xs border bg-white object-contain p-0.5" :src="logoUrl" />
                    <h3 class="text-left font-semibold">{{ t('pages.loyalty.create.instructions.pongoStore') }}</h3>
                  </div>
                </th>
                <th class="py-2">{{ t('pages.loyalty.create.instructions.associate') }}</th>
              </tr>
              <tr v-for="device in partnerDevices" :key="device.id">
                <td class="py-2">
                  <AppFormInput :id="device.id" v-model="device.readableName" class="relative z-10" :disabled="true" />
                </td>
                <td class="relative w-28">
                  <div class="absolute top-1/2 z-0 w-full border-t border-dashed border-border" />
                  <div
                    class="absolute left-1/2 top-1/2 z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full border bg-white p-1"
                  >
                    <BaseIcon name="HeroArrowsRightLeftIconOutline" class="h-5 w-5 text-foreground-disabled" />
                  </div>
                </td>
                <td class="py-2 pr-24">
                  <ComboboxSelect
                    v-model="selectedStores[device.id]"
                    value-key="id"
                    label-key="text"
                    trigger-class="w-full"
                    :placeholder="selectedStores[device.id]?.name"
                    :options="storesValues"
                    @update:model-value="updateStore(device.id, $event)"
                  />
                </td>
                <td class="py-2">
                  <BaseSwitch
                    id="association"
                    is-reversed
                    :model-value="isAssociatedArray.includes(device.id)"
                    :value="isAssociatedArray[device.id] ? true : null"
                    @click="handleDeviceAssociation(device.id)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="my-5 flex cursor-pointer items-center justify-center text-primary" @click="handleRefresh">
        <BaseIcon name="HeroArrowPathIconOutline" class="mr-2 w-6" />
        <span class="text-sm">{{ t('pages.loyalty.create.emptyDevices.button') }}</span>
      </div>
    </div>
  </LoyaltyCreateContainer>
</template>
